import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  ShieldCheck, FileText, ScaleIcon, Languages, ArrowUpRight,
  Clock, UserPlus, Briefcase,
} from 'lucide-react';
import BrandLogo from '../../components/common/BrandLogo';
import { toast } from 'sonner';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const NAVY = '#001d4c';
const MINT = '#4dffaa';

const TRUST_ITEMS = [
  { icon: Languages, label: 'Vereidigte Dolmetscher in über 50 Sprachen' },
  { icon: ScaleIcon, label: 'JVEG-konforme Abrechnung' },
  { icon: ShieldCheck, label: 'DSGVO-konform · vertrauliche Übertragung' },
  { icon: FileText, label: 'Automatisches Einsatzprotokoll als PDF' },
];

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success('Anmeldung erfolgreich');
      const home = user.role === 'admin' || user.role === 'pm' ? '/admin'
        : user.role === 'customer' ? '/customer'
        : '/interpreter';
      navigate(home);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FAFBFC]">
      {/* Top bar with brand + link back to landing page */}
      <header className="border-b border-slate-200/70 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <a href="https://www.dolmetsch-direkt.de" className="flex items-center" data-testid="header-logo-link">
            <BrandLogo height={32} />
          </a>
          <a
            href="https://www.dolmetsch-direkt.de"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-[#001d4c] transition-colors"
            data-testid="link-to-website"
          >
            Zur Webseite
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </header>

      {/* Main 2-column layout */}
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">

          {/* ============ LEFT: Brand / hero ============ */}
          <section className="space-y-8">
            {/* Pill badge */}
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-700">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: MINT }}></span>
              Portal-Login · DolmetschDirekt
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-[3.4rem] font-bold leading-[1.05] tracking-tight text-[#001d4c]">
              Willkommen im{' '}
              <span className="relative inline-block">
                Portal
                <span
                  className="absolute -bottom-1 left-0 right-0 h-2 rounded-sm opacity-80"
                  style={{ background: MINT }}
                ></span>
              </span>
              .
            </h1>

            <p className="text-lg text-slate-600 leading-relaxed max-w-xl">
              Die digitale Plattform für rechtssichere Dolmetschtermine — strukturiert,
              sicher und DSGVO-orientiert. Buchen Sie Einsätze, verwalten Sie Termine
              und greifen Sie auf Einsatzprotokolle zu.
            </p>

            {/* Trust list — matches landing-page style */}
            <ul className="grid sm:grid-cols-2 gap-3 max-w-xl">
              {TRUST_ITEMS.map(({ icon: Icon, label }) => (
                <li
                  key={label}
                  className="flex items-start gap-3 bg-white border border-slate-200/80 rounded-lg px-4 py-3"
                >
                  <span
                    className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(0, 29, 76, 0.06)' }}
                  >
                    <Icon className="w-4 h-4" style={{ color: NAVY }} />
                  </span>
                  <span className="text-sm text-slate-800 leading-snug pt-1">{label}</span>
                </li>
              ))}
            </ul>

            {/* Availability badge */}
            <div className="inline-flex items-center gap-3 bg-white border border-slate-200/80 rounded-lg px-4 py-3 shadow-sm">
              <span className="relative flex w-2.5 h-2.5">
                <span
                  className="absolute inset-0 rounded-full animate-ping opacity-60"
                  style={{ background: MINT }}
                ></span>
                <span
                  className="relative rounded-full w-2.5 h-2.5"
                  style={{ background: MINT }}
                ></span>
              </span>
              <div className="text-sm">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  Aktiv erreichbar
                </div>
                <div className="font-medium text-[#001d4c] flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> Mo–Fr 8:00–18:00
                </div>
              </div>
            </div>
          </section>

          {/* ============ RIGHT: Login card ============ */}
          <section className="lg:sticky lg:top-12">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_1px_3px_rgba(0,0,0,0.02),0_20px_50px_-20px_rgba(0,29,76,0.12)] p-8 lg:p-10">
              <h2 className="font-heading text-2xl lg:text-3xl font-bold text-[#001d4c] tracking-tight">
                Anmelden
              </h2>
              <p className="text-sm text-slate-500 mt-1.5 mb-7">
                Willkommen zurück. Bitte melden Sie sich mit Ihren Zugangsdaten an.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                <div>
                  <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1.5 block">
                    E-Mail
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ihre@email.de"
                    autoComplete="email"
                    data-testid="login-email-input"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3.5 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-[#001d4c]/20 focus:border-[#001d4c] outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1.5 block">
                    Passwort
                  </label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    data-testid="login-password-input"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3.5 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-[#001d4c]/20 focus:border-[#001d4c] outline-none transition-colors"
                  />
                </div>
                <div className="flex justify-end">
                  <Link
                    to="/forgot-password"
                    className="text-xs font-medium text-[#001d4c] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Passwort vergessen?
                  </Link>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#001d4c] hover:bg-[#0a2c63] text-white font-semibold rounded-lg px-4 py-3 text-sm transition-all disabled:opacity-60 shadow-sm hover:shadow-md"
                >
                  {loading ? 'Anmelden...' : 'Anmelden'}
                  {!loading && <ArrowUpRight className="w-4 h-4" />}
                </button>
              </form>

              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-slate-200"></div>
                <span className="text-[11px] text-slate-400 uppercase tracking-[0.18em]">Oder</span>
                <div className="flex-1 h-px bg-slate-200"></div>
              </div>

              <button
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
                className="w-full flex items-center justify-center gap-2.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-medium rounded-lg px-4 py-3 text-sm transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Mit Google anmelden
              </button>

              {/* Registration block */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <div className="text-sm font-semibold text-[#001d4c] text-center mb-1">
                  Noch kein Konto?
                </div>
                <div className="text-xs text-slate-500 text-center mb-4">
                  Jetzt kostenlos registrieren und loslegen
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Link
                    to="/register/customer"
                    data-testid="link-register-customer"
                    className="group inline-flex items-center justify-center gap-2 text-sm bg-[#001d4c] hover:bg-[#0a2c63] text-white rounded-lg px-4 py-3 font-semibold text-center transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5"
                  >
                    <Briefcase className="w-4 h-4" />
                    Als Kunde registrieren
                  </Link>
                  <Link
                    to="/register/interpreter"
                    data-testid="link-register-interpreter"
                    className="group inline-flex items-center justify-center gap-2 text-sm bg-[#4dffaa] hover:bg-[#3ce896] text-[#001d4c] rounded-lg px-4 py-3 font-semibold text-center transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5"
                  >
                    <UserPlus className="w-4 h-4" />
                    Als Dolmetscher registrieren
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200/70 bg-white mt-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} DolmetschDirekt · Multilingua Translation Service GmbH</div>
          <div className="flex items-center gap-4">
            <a href="https://www.dolmetsch-direkt.de/datenschutz" className="hover:text-[#001d4c] transition-colors">Datenschutz</a>
            <a href="https://www.dolmetsch-direkt.de/impressum" className="hover:text-[#001d4c] transition-colors">Impressum</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Login;
